import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
    paddingLeft: 10,
    width: "100%",
  },
  valuesRow: {
    flexDirection: "row",
    marginBottom: 8,
    justifyContent: "space-between",
    width: "100%",
  },
  leftContainer: {
    flex: 1,
    alignItems: "flex-start",
  },
  rightContainer: {
    flex: 1,
    alignItems: "flex-end",
  },
  valueTextLeft: {
    fontSize: 10,
    color: "#0A1A53",
    textAlign: "left",
  },
  valueTextRight: {
    fontSize: 10,
    color: "#0A1A53",
    textAlign: "right",
  },
  barContainer: {
    flexDirection: "row",
    height: 15,
    borderRadius: 12,
    overflow: "hidden",
  },
  barSegment: {
    height: "100%",
  },

  description: {
    color: "#0A1A53",
    fontSize: 9,
    marginTop: 4,
  },
});

type ValueData = {
  count: number;
  percentage: number;
};

type DualSegmentDistributionBlockProps = {
  leftValue: ValueData;
  rightValue: ValueData;
  leftTranslationKey?: string;
  rightTranslationKey?: string;
  leftColor?: string;
  rightColor?: string;
  description?: ReactNode;
};

export const DualSegmentDistributionBlock = ({
  leftValue,
  rightValue,
  leftTranslationKey,
  rightTranslationKey,
  leftColor = "#0A1A53",
  rightColor = "#0A1A53",
  description,
}: DualSegmentDistributionBlockProps) => {
  const { t } = useTranslation();

  // Normaliser les pourcentages pour qu'ils soient des entiers et totalisent 100
  const normalizePercentages = (left: number, right: number): { left: number; right: number } => {
    const leftFloored = Math.floor(left);
    const rightFloored = Math.floor(right);
    const leftRemainder = left - leftFloored;
    const rightRemainder = right - rightFloored;

    const flooredSum = leftFloored + rightFloored;
    const difference = 100 - flooredSum;

    // Distribuer la différence à la valeur avec le plus grand reste
    if (difference > 0) {
      if (leftRemainder >= rightRemainder) {
        return { left: leftFloored + 1, right: rightFloored };
      } else {
        return { left: leftFloored, right: rightFloored + 1 };
      }
    }

    return { left: leftFloored, right: rightFloored };
  };

  const normalized = normalizePercentages(leftValue.percentage, rightValue.percentage);

  // Calculer les largeurs en pourcentage pour chaque segment
  const totalPercentage = normalized.left + normalized.right;
  const leftWidth = totalPercentage > 0 ? (normalized.left / totalPercentage) * 100 : 0;
  const rightWidth = totalPercentage > 0 ? (normalized.right / totalPercentage) * 100 : 0;

  const formatValue = (value: ValueData, percentage: number, translationKey?: string) => {
    if (translationKey) {
      return t(translationKey, {
        count: value.count,
        percentage,
      });
    }
    return `${value.count} (${percentage}%)`;
  };

  return (
    <View style={styles.container}>
      <View style={styles.valuesRow}>
        <View style={styles.leftContainer}>
          <Text style={[styles.valueTextLeft, { color: leftColor }]}>
            {formatValue(leftValue, normalized.left, leftTranslationKey)}
          </Text>
        </View>
        <View style={styles.rightContainer}>
          <Text style={[styles.valueTextRight, { color: rightColor }]}>
            {formatValue(rightValue, normalized.right, rightTranslationKey)}
          </Text>
        </View>
      </View>
      <View style={styles.barContainer}>
        <View
          style={[
            styles.barSegment,
            {
              width: `${leftWidth}%`,
              backgroundColor: leftColor,
            },
          ]}
        />
        <View
          style={[
            styles.barSegment,
            {
              width: `${rightWidth}%`,
              backgroundColor: rightColor,
            },
          ]}
        />
      </View>
      <Text style={styles.description}>{description}</Text>
    </View>
  );
};
